import axios from 'axios';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const authTokenStorageKey = 'workflowshortcuts.googleCredential';
const visitorIdStorageKey = 'workflowshortcuts.visitorId';

function createVisitorId() {
  if (window.crypto?.randomUUID) {
    return `visitor_${window.crypto.randomUUID()}`;
  }

  return `visitor_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
}

export function getVisitorId() {
  let visitorId = window.localStorage.getItem(visitorIdStorageKey) || '';

  if (!visitorId) {
    visitorId = createVisitorId();
    window.localStorage.setItem(visitorIdStorageKey, visitorId);
  }

  return visitorId;
}

export function getAuthToken() {
  return window.localStorage.getItem(authTokenStorageKey) || '';
}

export function setAuthToken(token) {
  if (token) {
    window.localStorage.setItem(authTokenStorageKey, token);
    return;
  }

  window.localStorage.removeItem(authTokenStorageKey);
}

export function getErrorMessage(error) {
  const response = error?.response;
  const serverMessage = response?.data?.error;

  if (serverMessage) {
    return serverMessage;
  }

  if (response?.status === 403) {
    return 'This board is private. Sign in or open your saved playground to continue.';
  }

  if (response?.status === 404) {
    return 'That board or document could not be found.';
  }

  return error?.message || 'Something went wrong. Please try again.';
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  const visitorId = getVisitorId();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (visitorId) {
    config.headers['X-WorkflowShortcuts-Visitor'] = visitorId;
  }

  return config;
});

function normalizeList(data) {
  return Array.isArray(data) ? data : [];
}

export function createResourceService(resourceName) {
  const basePath = `/${resourceName}`;

  return {
    async list() {
      const response = await api.get(basePath);
      return normalizeList(response.data);
    },

    async get(id) {
      const response = await api.get(`${basePath}/${id}`);
      return response.data;
    },

    async create(payload) {
      const response = await api.post(basePath, payload);
      return response.data;
    },

    async update(id, payload) {
      const response = await api.patch(`${basePath}/${id}`, payload);
      return response.data;
    },

    async remove(id) {
      await api.delete(`${basePath}/${id}`);
    },
  };
}

export default api;
